<script lang="ts" setup>
import { computed, useTemplateRef } from 'vue';
import type { StripeCardElementChangeEvent } from '@stripe/stripe-js';
import { StripeElements, StripeElement } from 'vue-stripe-js';
import type { MppStripeInstance, MppPaymentCardElement } from '@Use/useMppPayment';

const props = defineProps<{
    stripeKey: string;
    cardHasError: boolean;
    cardErrorMessage?: string | null;
}>();

const emit = defineEmits<{
    change: [payload: { complete: boolean; errorMessage: string | null }];
}>();

const stripeElementsRef = useTemplateRef('stripeElements');
const cardComponentRef = useTemplateRef('cardComponent');

const cardOptions = {
    hidePostalCode: true,
    style: {
        base: {
            fontSize: '16px',
            color: '#1a1a1a',
            backgroundColor: 'transparent',
            '::placeholder': { color: '#9ca3af' },
        },
        invalid: { color: '#dc2626' },
    },
};

const fieldStatus = computed(() => props.cardHasError ? 'error' : 'valid');

const fieldMessage = computed(() => {
    if (!props.cardHasError) return '';
    return props.cardErrorMessage || 'Card details are required';
});

const onCardChange = (event: StripeCardElementChangeEvent) => {
    emit('change', {
        complete: event.complete,
        errorMessage: event.error?.message ?? null,
    });
};

defineExpose({
    get stripeInstance(): MppStripeInstance | null {
        return (stripeElementsRef.value as { instance?: MppStripeInstance } | null)?.instance ?? null;
    },
    get cardElement(): object | undefined {
        return (cardComponentRef.value as MppPaymentCardElement | null)?.stripeElement;
    },
});
</script>

<template>
    <app-form-field
        label="Card details"
        :is-optional="false"
        :field-status="fieldStatus"
        :message="fieldMessage"
        :legacy-mode="false"
        prefix=""
        data-testid="mpp-card-field"
    >
        <div
            class="mpp-card-field__wrapper"
            :class="{ 'mpp-card-field__wrapper--invalid': cardHasError }"
            data-testid="mpp-card-wrapper"
        >
            <stripe-elements
                ref="stripeElements"
                :stripe-key="stripeKey"
            >
                <stripe-element
                    ref="cardComponent"
                    type="card"
                    :options="cardOptions"
                    @change="onCardChange"
                />
            </stripe-elements>
        </div>
    </app-form-field>
</template>

<style lang="scss" scoped>
@use "certifid-v2-client-hub-styles" as *;

.mpp-card-field {
    &__wrapper {
        padding: 0.875rem 0.75rem;
        border: 1px solid $dark-gray;
        border-radius: 0.375rem;
        background: $white;

        &--invalid {
            border-color: $red;
        }
    }
}
</style>
